import { create } from "zustand";

import { readLocalStorage, removeLocalStorage, safeJsonParse, writeLocalStorage } from "@/lib/storage";

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: string;
  roleDisplayName?: string | null;
  profile: string;
  accessLevel?: "read" | "read_write" | "admin" | null;
  permissions: string[];
}

export type AdminLocale = "es" | "en";

interface AdminState {
  locale: AdminLocale;
  powerBIWorkspaceId: string | null;
  searchQuery: string;
  sidebarCollapsed: boolean;
  sessionToken: string | null;
  user: AdminUser | null;
  hydrated: boolean;
  hydrate: () => void;
  setLocale: (locale: AdminLocale) => void;
  setPowerBIWorkspaceId: (workspaceId: string | null) => void;
  setSearchQuery: (value: string) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  signIn: (payload: { sessionToken: string; user: AdminUser }) => void;
  signOut: () => void;
}

const STORAGE_KEY = "aquaportal-admin-session";
const LOCALE_KEY = "aquaportal-admin-locale";
const SIDEBAR_COLLAPSED_KEY = "aquaportal-admin-sidebar-collapsed";
const POWER_BI_WORKSPACE_KEY = "aquaportal-admin-powerbi-workspace";

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isAdminUser(value: unknown): value is AdminUser {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  const accessLevel = candidate.accessLevel;
  return (
    typeof candidate.id === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.email === "string" &&
    typeof candidate.role === "string" &&
    (candidate.roleDisplayName == null || typeof candidate.roleDisplayName === "string") &&
    typeof candidate.profile === "string" &&
    (accessLevel == null || accessLevel === "read" || accessLevel === "read_write" || accessLevel === "admin") &&
    isStringArray(candidate.permissions)
  );
}

function isStoredSession(value: unknown): value is { sessionToken: string; user: AdminUser } {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return typeof candidate.sessionToken === "string" && isAdminUser(candidate.user);
}

export const useAdminStore = create<AdminState>((set, get) => ({
  locale: "es",
  powerBIWorkspaceId: null,
  searchQuery: "",
  sidebarCollapsed: false,
  sessionToken: null,
  user: null,
  hydrated: false,
  hydrate: () => {
    if (get().hydrated) {
      return;
    }
    const localeRaw = readLocalStorage(LOCALE_KEY);
    const savedLocale = localeRaw === "en" ? "en" : "es";
    const savedSidebarCollapsed = readLocalStorage(SIDEBAR_COLLAPSED_KEY) === "true";
    const savedPowerBIWorkspaceId = readLocalStorage(POWER_BI_WORKSPACE_KEY)?.trim() || null;
    const parsedSession = safeJsonParse(readLocalStorage(STORAGE_KEY));
    const saved = isStoredSession(parsedSession) ? parsedSession : null;
    set({
      locale: savedLocale,
      powerBIWorkspaceId: savedPowerBIWorkspaceId,
      sidebarCollapsed: savedSidebarCollapsed,
      sessionToken: saved?.sessionToken ?? null,
      user: saved?.user ?? null,
      hydrated: true,
    });
  },
  setLocale: (locale) => {
    writeLocalStorage(LOCALE_KEY, locale);
    set({ locale });
  },
  setPowerBIWorkspaceId: (workspaceId) => {
    const normalized = workspaceId?.trim() || null;
    if (normalized) {
      writeLocalStorage(POWER_BI_WORKSPACE_KEY, normalized);
    } else {
      removeLocalStorage(POWER_BI_WORKSPACE_KEY);
    }
    set({ powerBIWorkspaceId: normalized });
  },
  setSearchQuery: (value) => {
    set({ searchQuery: value });
  },
  setSidebarCollapsed: (collapsed) => {
    writeLocalStorage(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    set({ sidebarCollapsed: collapsed });
  },
  signIn: ({ sessionToken, user }) => {
    writeLocalStorage(STORAGE_KEY, JSON.stringify({ sessionToken, user }));
    set({ sessionToken, user });
  },
  signOut: () => {
    removeLocalStorage(STORAGE_KEY);
    set({ sessionToken: null, user: null });
  },
}));
